import { reject } from "es-toolkit/compat";
import type React from "react";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import EditableSetup, { type AppMetadata, type Tool, type ToolPayload } from "./EditableSetup";
import EditableToolEdit from "./EditableToolEdit";
import { useEditableTool, type UseEditableToolProps, type UseEditableToolResult } from "./useEditableTool.vm";

export type EditableToolProps = {
  appMetadata: AppMetadata;
  classId: string;
  mountPoint: string;
  toolType: string;
  title?: string;

  tools: Tool[];
  suggestedTools?: Tool[];

  displayOpen: (path: string, displayPayload?: ToolkitAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  createTool: (data: { classId: string; type: string; payload: ToolPayload; dojoId?: string }) => void;
  editTool: (data: { classId: string; _id: string; payload: ToolPayload; updatedAt: string }) => void;
  deleteTool: (data: { _id: string; classId: string }) => void;

  setFocusOnParent?: (focus: boolean) => void;
  focusOnParent?: boolean;

  getDisplayPayload: (tool: Tool) => ToolkitAction;

  renderEditItem: ({
    onEdit,
    payload,
  }: {
    onEdit: (payload: ToolPayload, disabled?: boolean) => void;
    payload: ToolPayload;
  }) => React.ReactElement;
  renderItem: ({
    tool,
    openEditScreen,
    showTool,
  }: {
    tool: Tool;
    openEditScreen: (tool: Tool) => void;
    showTool: (tool: Tool) => void;
  }) => React.ReactElement;
  renderResult: ({ tool }: { tool: Tool }) => React.ReactElement;

  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state. Production callers leave the
   * default.
   */
  di?: {
    useVm?: (props: UseEditableToolProps) => UseEditableToolResult;
  };
};

export default function EditableTool(props: EditableToolProps) {
  const {
    appMetadata,
    classId,
    mountPoint,
    toolType,
    title,
    tools: toolsProp,
    suggestedTools,
    displayOpen,
    displayClose,
    remoteClose,
    createTool: createToolApi,
    editTool: editToolApi,
    deleteTool: deleteToolApi,
    getDisplayPayload,
    setFocusOnParent,
    focusOnParent,
    renderEditItem,
    renderItem,
    renderResult,
    di,
  } = props;

  const useVm = di?.useVm ?? useEditableTool;
  const {
    editScreen,
    selectedTool,
    openEditScreen,
    closeEditScreen,
    showTool,
    clearSelectedTool,
    createTool,
    editTool,
    deleteTool,
  } = useVm({
    classId,
    toolType,
    createToolApi,
    editToolApi,
    deleteToolApi,
    displayOpen,
    getDisplayPayload,
  });

  let tools = toolsProp;
  if (tools && suggestedTools) {
    const filteredSuggestions = reject(suggestedTools, ({ dojoId }) => tools.map((t) => t.dojoId).includes(dojoId));
    tools = tools.concat(filteredSuggestions);
  }

  if (editScreen) {
    return (
      <EditableToolEdit
        renderEditItem={renderEditItem}
        closeEditScreen={closeEditScreen}
        appMetadata={appMetadata}
        createTool={createTool}
        editTool={editTool}
        deleteTool={deleteTool}
        tool={selectedTool}
        // Spread the payload to the top level so renderResult can read
        // `tool.title` / `tool.list` directly — matches the flat-Tool shape
        // EditableSetup produces for saved tools. Inline arrow re-allocates
        // each render; left bare because EditableToolEdit doesn't memoize on
        // showTool and EditableTool rarely re-renders. Promote to useCallback
        // if either of those changes.
        showTool={(payload) => showTool({ ...payload, payload })}
        focusOnParent={focusOnParent}
        setFocusOnParent={setFocusOnParent}
      />
    );
  }
  if (selectedTool) {
    return (
      <AppModal
        {...appMetadata}
        onRequestHide={() => {
          displayClose();
          clearSelectedTool();
          remoteClose();
        }}
        onRequestStart={mountPoint === "mobile" ? () => displayOpen(`/apps/display/${toolType}`) : undefined}
        onRequestStop={mountPoint === "mobile" ? () => displayClose() : undefined}
      >
        {renderResult({ tool: selectedTool })}
      </AppModal>
    );
  }

  return (
    <AppPage {...appMetadata} classId={classId} mountPoint={mountPoint}>
      <EditableSetup
        renderItem={renderItem}
        openEditScreen={openEditScreen}
        tools={tools}
        title={title}
        showTool={showTool}
        appMetadata={appMetadata}
      />
    </AppPage>
  );
}
